import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  CATEGORIES,
  HAKLAI_PTOR_TYPES,
  RESIDENTIAL_LABELS,
  normalizeHaklaiCurrencyForDate
} from "../calc/shared/haklaiConstants";
import {
  getHaklaiLogicalPropertyPartsData,
  isHaklaiZabanAttributionRelevant
} from "../calc/shared/haklaiFlowHelpers";
import {
  createHaklaiPropertyPart,
  findNextResidentialSlot,
  getNextCategoryKey,
  removeHaklaiPropertyPartKeys
} from "../calc/propertyParts/logic/haklaiPropertyPartsHelpers";
import {
  createHaklaiExpense,
  DEFAULT_HAKLAI_EXPENSE_TYPE,
  DEFAULT_HAKLAI_ZABAN_ATTRIBUTION,
  normalizeHaklaiExpenseDepreciationByPart,
  normalizeHaklaiExpensesForDependencies,
  removeExpensesFromState
} from "../calc/expenses/logic/haklaiExpensesHelpers";
import {
  getHaklaiPartDepreciationMode,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  normalizeHaklaiDepreciationState
} from "../calc/shared/haklaiDepreciationHelpers";
import {
  normalizeHaklaiPurchaseDepreciationStateForLogicalParts
} from "../calc/shared/haklaiPurchaseDepreciationState";
import { getHaklaiPrisaMaxYears } from "../calc/prisa/haklaiPrisaHelpers";
import { calculateHaklaiPrisaCreditPointsForPerson } from "../calc/prisa/haklaiPrisaCreditPoints";
import {
  getHaklaiPrisaFieldErrorsForBlur,
  HAKLAI_PRISA_FIELD_KEYS
} from "../calc/prisa/haklaiPrisaValidation";
import {
  getHaklaiPropertyPartRemoveDependentText,
  HAKLAI_PROPERTY_PART_REMOVE_DIALOG,
  HAKLAI_PRISA_DISABLE_DIALOG,
  joinHaklaiPropertyPartRemoveDependencies
} from "./haklaiHookTexts";
import { sanitizePre1997ConstructionFlags } from "../calc/shared/haklaiRawPayloadHelpers";
import {
  normalizeHaklaiPropertyPartPtorSelections,
  sanitizeHaklaiPtorTypeForWay
} from "../calc/shared/haklaiPtorHelpers";
import { applyHaklaiPreCalcDependencyRules } from "../domain/haklaiCalcDependencies";
import {
  applyHaklaiPrisaDependencies,
  resizeHaklaiPrisaDataPerYear,
  resizeHaklaiPrisaPersonsData
} from "../domain/haklaiPrisaDependencies";

/**
 * Owns Haklai calculator state transitions.
 *
 * Responsibilities:
 * - applies cross-section dependency rules when users edit fields
 * - normalizes row add/remove/update behavior before state is persisted
 * - keeps derived depreciation and prisa data in sync with source inputs
 *
 * Rendering and validation UI stay in section files; pure domain rules should
 * live in feature `logic/` helpers or `domain/`.
 */

const getHaklaiLogicalPropertyPartsForCalc = (calcState = {}) => (
  getHaklaiLogicalPropertyPartsData(
    calcState.mainCalc?.propertyPartsData || {},
    calcState.preCalc?.totalPropertyCost,
    calcState.preCalc?.hasZaban === true
  )
);

const hasRawValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const hasMeaningfulPrisaPersonData = (person = {}, defaultYearsCount = 0) => {
  if (hasRawValue(person.personName)) return true;
  if (hasRawValue(person.birthOfClient)) return true;
  if (hasRawValue(person.creditPointGender)) return true;
  if (hasRawValue(person.creditPointResidency)) return true;
  if (person.isMaximal === false) return true;

  const dataPerYear = Array.isArray(person.dataPerYear) ? person.dataPerYear : [];
  if (dataPerYear.length !== defaultYearsCount) return true;
  return dataPerYear.some((year = {}) => (
    hasRawValue(year.income)
    || (
      hasRawValue(year.zikui)
      && Number(year.zikui) !== 0
    )
  ));
};

const hasMeaningfulPrisaData = (prisa = {}, defaultYearsCount = 0) => {
  const personsData = Array.isArray(prisa.personsData) ? prisa.personsData : [];
  if (personsData.length > 1) return true;
  return personsData.some((person) => hasMeaningfulPrisaPersonData(person, defaultYearsCount));
};

const clearExpenseDepreciationForPart = (expenses = [], partKey = "") => {
  if (!partKey || !Array.isArray(expenses)) return expenses;
  let changed = false;
  const nextExpenses = expenses.map((expense = {}) => {
    const depreciationByPart = (
      expense.depreciationByPart
      && typeof expense.depreciationByPart === "object"
      && !Array.isArray(expense.depreciationByPart)
    ) ? expense.depreciationByPart : {};
    if (!Object.prototype.hasOwnProperty.call(depreciationByPart, partKey)) return expense;
    changed = true;
    const nextDepreciationByPart = { ...depreciationByPart };
    delete nextDepreciationByPart[partKey];
    return { ...expense, depreciationByPart: nextDepreciationByPart };
  });
  return changed ? nextExpenses : expenses;
};

const useHaklaiCalcActions = ({
  calc,
  setCalc,
  setDealDate,
  setIsDealDateChanged
}) => {
  const [touchedPrisaFields, setTouchedPrisaFields] = useState({});

  const handlePreCalcOnChange = useCallback(
    (e) => {
      let val = e.target.value;
      if (val === "true") val = true;
      if (val === "false") val = false;
      setCalc((prev) => {
        const next = applyHaklaiPreCalcDependencyRules(prev, e.target.name, val);
        const parcelField =
          e.target.name === "parcelGush"
          || e.target.name === "parcelHelka"
          || e.target.name === "parcelAddressSearch";
        if (parcelField && next.preCalc.parcelLookupError) {
          return { ...next, preCalc: { ...next.preCalc, parcelLookupError: null } };
        }
        return next;
      });
      if (e.target.name === "soldPropertyDate") {
        setDealDate(e.target.value);
        setIsDealDateChanged(true);
      }
    },
    [setCalc, setDealDate, setIsDealDateChanged]
  );

  const handleAcquisitionDetailsOnChange = useCallback(
    (e) => {
      let val = e.target.value;
      if (val === "true") val = true;
      if (val === "false") val = false;
      const fieldName = e.target.name;
      const affectsPrisaSpan =
        fieldName === "deduDate"
        || fieldName === "testatorPassingDate"
        || fieldName === "testatorBoughtPropertyDate";
      setCalc((prev) => {
        const acquisitionDetails = {
          ...prev.preCalc.acquisitionDetails,
          [fieldName]: val
        };
        const next = {
          ...prev,
          preCalc: {
            ...prev.preCalc,
            acquisitionDetails
          }
        };
        const nextWithPrisa = affectsPrisaSpan ? applyHaklaiPrisaDependencies(next) : next;
        const sanitizedPre1997State = sanitizePre1997ConstructionFlags(
          nextWithPrisa.mainCalc?.propertyPartsData || {},
          nextWithPrisa.preCalc || {}
        );
        if (!sanitizedPre1997State.changed) return nextWithPrisa;
        return {
          ...nextWithPrisa,
          mainCalc: {
            ...nextWithPrisa.mainCalc,
            propertyPartsData: sanitizedPre1997State.propertyPartsData
          }
        };
      });
    },
    [setCalc]
  );

  const handleMainCalcPrisaOnChange = useCallback((e, personIndex, yearIndex) => {
    let val = e.target.value;
    if (val === "true") val = true;
    if (val === "false") val = false;
    const fieldName = e.target.name;
    const applyPrisaChange = () => {
      setCalc((prev) => {
        const currentPrisa = prev.mainCalc.prisa || { shouldTryPrisa: false, personsData: [] };
        const maxYears = getHaklaiPrisaMaxYears(prev.preCalc);
        const currentPersonsData = Array.isArray(currentPrisa.personsData) ? currentPrisa.personsData : [];
        let nextPrisa = {
          ...currentPrisa,
          personsData: currentPersonsData.map((p) => ({
            ...p,
            dataPerYear: (Array.isArray(p.dataPerYear) ? p.dataPerYear : []).map((y) => ({ ...y }))
          }))
        };

        if (fieldName === "shouldTryPrisa") {
          nextPrisa.shouldTryPrisa = val;
          nextPrisa = resizeHaklaiPrisaPersonsData(nextPrisa, val ? 1 : 0, maxYears);
        } else if (fieldName === "personsData") {
          nextPrisa = resizeHaklaiPrisaPersonsData(nextPrisa, val, maxYears);
        } else if (fieldName === "dataPerYear") {
          const pIdx = personIndex || 0;
          nextPrisa.personsData[pIdx] = resizeHaklaiPrisaDataPerYear(nextPrisa.personsData[pIdx], val);
        } else if (fieldName === "income") {
          const pIdx = personIndex || 0;
          const yIdx = yearIndex || 0;
          nextPrisa.personsData[pIdx].dataPerYear[yIdx][fieldName] = val;
        } else if (fieldName === "zikui") {
          const pIdx = personIndex || 0;
          if (Number.isInteger(yearIndex)) {
            nextPrisa.personsData[pIdx].dataPerYear[yearIndex][fieldName] = val;
          } else {
            nextPrisa.personsData[pIdx].dataPerYear = (nextPrisa.personsData[pIdx].dataPerYear || [])
              .map((year, currentYearIndex) => ({
                ...year,
                zikui: currentYearIndex === 0 && nextPrisa.personsData[pIdx].isMaximal
                  ? year.zikui
                  : val
              }));
          }
        } else if (fieldName === "isMaximal") {
          const pIdx = personIndex || 0;
          nextPrisa.personsData[pIdx][fieldName] = val;
        } else {
          const pIdx = personIndex || 0;
          nextPrisa.personsData[pIdx][fieldName] = val;
        }

        return {
          ...prev,
          mainCalc: { ...prev.mainCalc, prisa: nextPrisa }
        };
      });
      if (fieldName === "shouldTryPrisa") {
        if (val === false) {
          setTouchedPrisaFields({});
        } else {
          setTouchedPrisaFields((prev) => ({
            ...prev,
            [HAKLAI_PRISA_FIELD_KEYS.shouldTryPrisa]: true
          }));
        }
      }
    };

    if (
      fieldName === "shouldTryPrisa"
      && val === false
      && calc.mainCalc?.prisa?.shouldTryPrisa === true
      && hasMeaningfulPrisaData(calc.mainCalc?.prisa, getHaklaiPrisaMaxYears(calc.preCalc))
    ) {
      Swal.fire({
        title: HAKLAI_PRISA_DISABLE_DIALOG.title,
        text: HAKLAI_PRISA_DISABLE_DIALOG.text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: HAKLAI_PRISA_DISABLE_DIALOG.confirm,
        cancelButtonText: HAKLAI_PRISA_DISABLE_DIALOG.cancel,
        reverseButtons: true
      }).then((result) => {
        if (!result.isConfirmed) return;
        applyPrisaChange();
      });
      return;
    }

    applyPrisaChange();
  }, [calc.mainCalc?.prisa, calc.preCalc, setCalc]);

  const handlePrisaCreditPointsApply = useCallback((personIndex = 0) => {
    setCalc((prev) => {
      const currentPrisa = prev.mainCalc.prisa || { shouldTryPrisa: false, personsData: [] };
      const currentPersonsData = Array.isArray(currentPrisa.personsData) ? currentPrisa.personsData : [];
      const pIdx = personIndex || 0;
      const person = currentPersonsData[pIdx];
      const creditPoints = calculateHaklaiPrisaCreditPointsForPerson(person);
      if (creditPoints === null || !person) return prev;

      const personsData = currentPersonsData.map((currentPerson, index) => (
        index === pIdx
          ? {
              ...currentPerson,
              dataPerYear: (Array.isArray(currentPerson.dataPerYear) ? currentPerson.dataPerYear : [])
                .map((year, yearIndex) => ({
                  ...year,
                  zikui: yearIndex === 0 && currentPerson.isMaximal
                    ? year.zikui
                    : creditPoints
                }))
            }
          : {
              ...currentPerson,
              dataPerYear: (Array.isArray(currentPerson.dataPerYear) ? currentPerson.dataPerYear : [])
                .map((year) => ({ ...year }))
            }
      ));

      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          prisa: {
            ...currentPrisa,
            personsData
          }
        }
      };
    });
  }, [setCalc]);

  const handleMainCalcAnotherQuestionsOnChange = useCallback((e) => {
    let val = e.target.value;
    if (val === "true") val = true;
    if (val === "false") val = false;
    setCalc((prev) => ({
      ...prev,
      mainCalc: { ...prev.mainCalc, [e.target.name]: val }
    }));
  }, [setCalc]);

  const touchPrisaField = useCallback((fieldName) => {
    if (!fieldName) return;
    setTouchedPrisaFields((prev) => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
  }, []);

  const prisaFieldErrors = useMemo(() => {
    return getHaklaiPrisaFieldErrorsForBlur(calc, touchedPrisaFields);
  }, [calc, touchedPrisaFields]);

  const handlePropertyPartOnChange = useCallback(
    (componentKey, fieldName, value) => {
      setCalc((prev) => {
        const existing = prev.mainCalc.propertyPartsData[componentKey];
        if (!existing) return prev;
        const currentPropertyPartsData = prev.mainCalc.propertyPartsData || {};
        if (fieldName === "ptorType") {
          const nextPtorType = sanitizeHaklaiPtorTypeForWay(
            value,
            prev.preCalc?.wayOfGettingProperty
          );
          const nextPropertyPartsData = Object.entries(currentPropertyPartsData).reduce((acc, [partKey, part]) => {
            if (part?.category !== CATEGORIES.RESIDENTIAL) {
              acc[partKey] = part;
              return acc;
            }
            const shouldClearOtherRows = nextPtorType !== HAKLAI_PTOR_TYPES.NONE;
            const rowPtorType = partKey === componentKey
              ? nextPtorType
              : shouldClearOtherRows
                ? HAKLAI_PTOR_TYPES.NONE
                : part.ptorType || HAKLAI_PTOR_TYPES.NONE;
            acc[partKey] = { ...part, ptorType: rowPtorType };
            return acc;
          }, {});
          const ptorState = normalizeHaklaiPropertyPartPtorSelections(
            nextPropertyPartsData,
            prev.preCalc || {},
            { applyLegacy: false }
          );
          const selectedPtorType = ptorState.selectedPtorType;
          const shouldKeepTzinun = (
            selectedPtorType === HAKLAI_PTOR_TYPES.SINGLE
            && prev.preCalc?.wayOfGettingProperty === "gift"
            && nextPtorType === HAKLAI_PTOR_TYPES.SINGLE
            && (existing.ptorType || HAKLAI_PTOR_TYPES.NONE) === HAKLAI_PTOR_TYPES.SINGLE
          );
          return {
            ...prev,
            preCalc: {
              ...prev.preCalc,
              ptorType: selectedPtorType,
              hasTzinunOnPtor: shouldKeepTzinun
                ? prev.preCalc?.hasTzinunOnPtor === true
                : false
            },
            mainCalc: {
              ...prev.mainCalc,
              propertyPartsData: ptorState.propertyPartsData
            }
          };
        }
        const nextPropertyPartsData = Object.entries(currentPropertyPartsData).reduce((acc, [partKey, part]) => {
          if (fieldName === "constructionsDoneBeforeApril1997" && value === true) {
            acc[partKey] = {
              ...part,
              constructionsDoneBeforeApril1997: partKey === componentKey
            };
            return acc;
          }
          acc[partKey] = partKey === componentKey ? { ...part, [fieldName]: value } : part;
          return acc;
        }, {});
        return {
          ...prev,
          mainCalc: {
            ...prev.mainCalc,
            propertyPartsData: nextPropertyPartsData
          }
        };
      });
    },
    [setCalc]
  );

  const handleExpenseOnChange = useCallback((expenseId, fieldName, value) => {
    setCalc((prev) => {
      const propertyPartsData = getHaklaiLogicalPropertyPartsForCalc(prev);
      const hasZaban = prev.preCalc?.hasZaban === true;
      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          expenses: (prev.mainCalc.expenses || []).map((expense) => {
            if (expense.id !== expenseId) return expense;
            if (fieldName === "isDepreciable" || fieldName === "depreciation") return expense;
            const updatedExpense = {
              ...expense,
              [fieldName]: fieldName === "depreciationByPart"
                ? normalizeHaklaiExpenseDepreciationByPart(value, null, propertyPartsData, expense.expenseType || "")
                : value
            };
            if (fieldName === "expenseType") {
              updatedExpense.depreciationByPart = normalizeHaklaiExpenseDepreciationByPart(
                updatedExpense.depreciationByPart,
                null,
                propertyPartsData,
                value || ""
              );
            }
            if (fieldName === "expenseDate") {
              updatedExpense.expenseCurrency = normalizeHaklaiCurrencyForDate(
                value,
                updatedExpense.expenseCurrency || "newShekel"
              );
            }
            if (fieldName === "targetPartKey") {
              if (value !== expense.targetPartKey) {
                delete updatedExpense.depreciationByPart;
              }
              const relevant = isHaklaiZabanAttributionRelevant(propertyPartsData, value, hasZaban);
              if (!relevant) {
                updatedExpense.zabanAttribution = "";
              } else if (!updatedExpense.zabanAttribution) {
                updatedExpense.zabanAttribution = DEFAULT_HAKLAI_ZABAN_ATTRIBUTION;
              }
            }
            return updatedExpense;
          })
        }
      };
    });
  }, [setCalc]);

  useEffect(() => {
    setCalc((prev) => {
      const normalizedExpensesState = normalizeHaklaiExpensesForDependencies(
        prev.mainCalc?.expenses || [],
        prev.mainCalc?.propertyPartsData || {},
        prev.preCalc?.hasZaban === true,
        prev.preCalc?.totalPropertyCost
      );
      const logicalPropertyPartsData = getHaklaiLogicalPropertyPartsForCalc(prev);
      const normalizedPurchaseDepreciationState = normalizeHaklaiPurchaseDepreciationStateForLogicalParts(
        prev.mainCalc || {},
        logicalPropertyPartsData
      );
      if (
        !normalizedExpensesState.changed
        && !normalizedPurchaseDepreciationState.changed
      ) return prev;
      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          expenses: normalizedExpensesState.changed
            ? normalizedExpensesState.expenses
            : prev.mainCalc?.expenses || [],
          purchaseDepreciationByPart: normalizedPurchaseDepreciationState.purchaseDepreciationByPart,
          purchaseDepreciationSelectionByPart: normalizedPurchaseDepreciationState.purchaseDepreciationSelectionByPart
        }
      };
    });
  }, [
    calc.preCalc?.hasZaban,
    calc.preCalc?.totalPropertyCost,
    calc.mainCalc?.propertyPartsData,
    setCalc
  ]);

  useEffect(() => {
    setCalc((prev) => {
      const sanitizedPre1997State = sanitizePre1997ConstructionFlags(
        prev.mainCalc?.propertyPartsData || {},
        prev.preCalc || {}
      );
      if (!sanitizedPre1997State.changed) return prev;
      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          propertyPartsData: sanitizedPre1997State.propertyPartsData
        }
      };
    });
  }, [
    calc.preCalc?.hasZaban,
    calc.preCalc?.wayOfGettingProperty,
    calc.preCalc?.acquisitionDetails?.deduDate,
    calc.preCalc?.acquisitionDetails?.testatorPassingDate,
    calc.preCalc?.acquisitionDetails?.testatorBoughtPropertyDate,
    calc.mainCalc?.propertyPartsData,
    setCalc
  ]);

  const handleAddExpense = useCallback((targetPartKey = "") => {
    setCalc((prev) => {
      const logicalPropertyPartsData = getHaklaiLogicalPropertyPartsForCalc(prev);
      const defaultZabanAttribution = isHaklaiZabanAttributionRelevant(
        logicalPropertyPartsData,
        targetPartKey,
        prev.preCalc?.hasZaban === true
      )
        ? DEFAULT_HAKLAI_ZABAN_ATTRIBUTION
        : "";

      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          expenses: [
            ...(prev.mainCalc.expenses || []),
            createHaklaiExpense(
              targetPartKey,
              defaultZabanAttribution,
              DEFAULT_HAKLAI_EXPENSE_TYPE
            )
          ]
        }
      };
    });
  }, [setCalc]);

  const handleRemoveExpenses = useCallback((expenseIds) => {
    const normalizedIds = Array.isArray(expenseIds) ? expenseIds.filter(Boolean) : [];
    if (!normalizedIds.length) return;

    setCalc((prev) => {
      const currentExpenses = prev.mainCalc.expenses || [];
      const nextExpenses = removeExpensesFromState(currentExpenses, normalizedIds);
      if (nextExpenses === currentExpenses) return prev;

      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          expenses: nextExpenses
        }
      };
    });
  }, [setCalc]);

  const handleRemoveExpense = useCallback((expenseId) => {
    handleRemoveExpenses([expenseId]);
  }, [handleRemoveExpenses]);

  const handlePurchaseDepreciationChange = useCallback((partKey, depreciation, depreciationMode = "") => {
    if (!partKey) return;
    setCalc((prev) => {
      const part = getHaklaiLogicalPropertyPartsForCalc(prev)[partKey];
      const resolvedMode = depreciationMode || getHaklaiPartDepreciationMode(part);
      const isResidentialRentalProfile = resolvedMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO;
      const isManualForm11Profile = resolvedMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
      const nextPurchaseDepreciationByPart = { ...(prev.mainCalc.purchaseDepreciationByPart || {}) };
      const nextResidentialRentalByPart = { ...(prev.mainCalc.residentialRentalByPart || {}) };
      const nextPurchaseDepreciationSelectionByPart = {
        ...(prev.mainCalc.purchaseDepreciationSelectionByPart || {})
      };
      const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
      let nextExpenses = prev.mainCalc.expenses;
      if (isResidentialRentalProfile) {
        delete nextPurchaseDepreciationByPart[partKey];
        if (Object.keys(normalizedDepreciation).length > 0) {
          nextResidentialRentalByPart[partKey] = normalizedDepreciation;
        } else {
          delete nextResidentialRentalByPart[partKey];
          delete nextPurchaseDepreciationSelectionByPart[partKey];
          nextExpenses = clearExpenseDepreciationForPart(nextExpenses, partKey);
        }

        return {
          ...prev,
          mainCalc: {
            ...prev.mainCalc,
            expenses: nextExpenses,
            residentialRentalByPart: nextResidentialRentalByPart,
            purchaseDepreciationByPart: nextPurchaseDepreciationByPart,
            purchaseDepreciationSelectionByPart: nextPurchaseDepreciationSelectionByPart
          }
        };
      }

      if (!isManualForm11Profile) return prev;
      if (part?.category === CATEGORIES.RESIDENTIAL) return prev;

      if (Object.keys(normalizedDepreciation).length > 0) {
        nextPurchaseDepreciationByPart[partKey] = normalizedDepreciation;
        if (part?.category === CATEGORIES.RESIDENTIAL) {
          delete nextResidentialRentalByPart[partKey];
        }
      } else {
        delete nextPurchaseDepreciationByPart[partKey];
        delete nextPurchaseDepreciationSelectionByPart[partKey];
      }

      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          residentialRentalByPart: nextResidentialRentalByPart,
          purchaseDepreciationByPart: nextPurchaseDepreciationByPart,
          purchaseDepreciationSelectionByPart: nextPurchaseDepreciationSelectionByPart
        }
      };
    });
  }, [setCalc]);

  const handlePurchaseDepreciationSelectionChange = useCallback((partKey, isSelected) => {
    if (!partKey) return;
    setCalc((prev) => {
      const nextPurchaseDepreciationSelectionByPart = {
        ...(prev.mainCalc.purchaseDepreciationSelectionByPart || {})
      };
      if (isSelected === true) {
        nextPurchaseDepreciationSelectionByPart[partKey] = true;
      } else {
        delete nextPurchaseDepreciationSelectionByPart[partKey];
      }
      return {
        ...prev,
        mainCalc: {
          ...prev.mainCalc,
          purchaseDepreciationSelectionByPart: nextPurchaseDepreciationSelectionByPart
        }
      };
    });
  }, [setCalc]);

  const handleAddRow = useCallback(
    (category, predefinedKey, customLabel) => {
      setCalc((prev) => {
        const parts = { ...prev.mainCalc.propertyPartsData };

        let key, label;
        if (category === CATEGORIES.RESIDENTIAL) {
          if (predefinedKey) {
            key = predefinedKey;
            label = customLabel || RESIDENTIAL_LABELS[parseInt(predefinedKey.split("_")[1]) - 1];
          } else {
            const slot = findNextResidentialSlot(parts);
            key = slot?.key || "";
            label = slot?.label || "";
          }
          if (!key || parts[key]) return prev;
        } else {
          key = getNextCategoryKey({ categoryId: category, propertyPartsData: parts });
          label = customLabel !== undefined ? customLabel : "";
          if (!key) return prev;
        }

        parts[key] = createHaklaiPropertyPart({ category, label });

        return {
          ...prev,
          mainCalc: {
            ...prev.mainCalc,
            propertyPartsData: parts,
            expenses: prev.mainCalc.expenses || [],
            residentialRentalByPart: prev.mainCalc.residentialRentalByPart || {},
            purchaseDepreciationByPart: prev.mainCalc.purchaseDepreciationByPart || {},
            purchaseDepreciationSelectionByPart: prev.mainCalc.purchaseDepreciationSelectionByPart || {},
            purchaseZabanAttributionByPart: {}
          }
        };
      });
    },
    [setCalc]
  );

  const handleRemoveRows = useCallback(
    (keys) => {
      const uniqueKeys = [...new Set((Array.isArray(keys) ? keys : []).filter(Boolean))];
      if (!uniqueKeys.length) return;

      setCalc((prev) => {
        const nextState = removeHaklaiPropertyPartKeys({
          propertyPartsData: prev.mainCalc.propertyPartsData || {},
          expenses: prev.mainCalc.expenses || [],
          residentialRentalByPart: prev.mainCalc.residentialRentalByPart || {},
          purchaseDepreciationByPart: prev.mainCalc.purchaseDepreciationByPart || {},
          purchaseDepreciationSelectionByPart: prev.mainCalc.purchaseDepreciationSelectionByPart || {},
          purchaseZabanAttributionByPart: {},
          keys: uniqueKeys,
          hasZaban: prev.preCalc?.hasZaban === true,
          totalPropertyCost: prev.preCalc?.totalPropertyCost
        });

        if (!nextState.changed) return prev;
        const ptorState = normalizeHaklaiPropertyPartPtorSelections(
          nextState.propertyPartsData,
          prev.preCalc || {},
          { applyLegacy: false }
        );
        const shouldKeepTzinun = (
          ptorState.selectedPtorType === HAKLAI_PTOR_TYPES.SINGLE
          && prev.preCalc?.wayOfGettingProperty === "gift"
        );

        return {
          ...prev,
          preCalc: {
            ...prev.preCalc,
            ptorType: ptorState.selectedPtorType,
            hasTzinunOnPtor: shouldKeepTzinun
              ? prev.preCalc?.hasTzinunOnPtor === true
              : false
          },
          mainCalc: {
            ...prev.mainCalc,
            propertyPartsData: ptorState.propertyPartsData,
            expenses: nextState.expenses,
            residentialRentalByPart: nextState.residentialRentalByPart,
            purchaseDepreciationByPart: nextState.purchaseDepreciationByPart,
            purchaseDepreciationSelectionByPart: nextState.purchaseDepreciationSelectionByPart,
            purchaseZabanAttributionByPart: nextState.purchaseZabanAttributionByPart
          }
        };
      });
    },
    [setCalc]
  );

  const getRemovalDependentSummary = useCallback((key) => {
    if (!key) return { expenseCount: 0, hasDepreciation: false };
    const expenseCount = (calc.mainCalc?.expenses || []).filter((expense) => (
      expense?.targetPartKey === key
    )).length;
    const depreciation = normalizeHaklaiDepreciationState(
      calc.mainCalc?.purchaseDepreciationByPart?.[key]
    );
    const residentialRental = normalizeHaklaiDepreciationState(
      calc.mainCalc?.residentialRentalByPart?.[key]
    );
    return {
      expenseCount,
      hasDepreciation: Object.keys(depreciation).length > 0 || Object.keys(residentialRental).length > 0
    };
  }, [
    calc.mainCalc?.expenses,
    calc.mainCalc?.purchaseDepreciationByPart,
    calc.mainCalc?.residentialRentalByPart
  ]);

  const handleRemoveRow = useCallback((key) => {
    if (!key) return;
    const dependentSummary = getRemovalDependentSummary(key);
    if (!dependentSummary.expenseCount && !dependentSummary.hasDepreciation) {
      handleRemoveRows([key]);
      return;
    }

    const dependentLabels = joinHaklaiPropertyPartRemoveDependencies([
      dependentSummary.expenseCount ? HAKLAI_PROPERTY_PART_REMOVE_DIALOG.expenseDependencies : "",
      dependentSummary.hasDepreciation ? HAKLAI_PROPERTY_PART_REMOVE_DIALOG.depreciationDependencies : ""
    ]);

    Swal.fire({
      title: HAKLAI_PROPERTY_PART_REMOVE_DIALOG.title,
      text: dependentLabels
        ? getHaklaiPropertyPartRemoveDependentText(dependentLabels)
        : HAKLAI_PROPERTY_PART_REMOVE_DIALOG.fallbackText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: HAKLAI_PROPERTY_PART_REMOVE_DIALOG.confirm,
      cancelButtonText: HAKLAI_PROPERTY_PART_REMOVE_DIALOG.cancel,
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) return;
      handleRemoveRows([key]);
    });
  }, [getRemovalDependentSummary, handleRemoveRows]);

  return {
    handleAcquisitionDetailsOnChange,
    handleAddExpense,
    handleAddRow,
    handleExpenseOnChange,
    handleMainCalcAnotherQuestionsOnChange,
    handleMainCalcPrisaOnChange,
    handlePrisaCreditPointsApply,
    handlePreCalcOnChange,
    handlePropertyPartOnChange,
    handlePurchaseDepreciationChange,
    handlePurchaseDepreciationSelectionChange,
    handleRemoveExpense,
    handleRemoveRow,
    prisaFieldErrors,
    touchPrisaField
  };
};

export default useHaklaiCalcActions;
